import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface InventoryFilters {
  location_id?: number | string; // Optional: filters inventory by subsidiary_tax_info.id
  item_id?: number;
  item_name?: string;
  serial_number?: string;
  location?: string;
  unit?: string;
  manufacturer?: string;
  show_low_stock?: boolean;
  show_near_expiry?: boolean;
  is_deleted?: boolean;
  positive_only?: boolean;
  get_services?: boolean;
  limit?: number;
  offset?: number;
  all_location?: boolean;
}

export interface InventoryApiResponse {
  data: {
    inventories?: any[];
    total?: number;
  };
  status?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class InventoryService {
  private readonly API_BASE_URL = environment.accountingUrl;

  constructor(private http: HttpClient) {}

  /**
   * Search/Get all inventories with optional filters
   * Endpoint: GET /api/v1/inventories/
   * Query Parameters: item_id, item_name, serial_number, location, is_deleted, positive_only, limit, offset, all_location
   */
  searchInventories(filters: InventoryFilters): Observable<InventoryApiResponse> {
    // location_id is mandatory unless all_location is set
    if (!filters.location_id && !filters.all_location) {
      throw new Error('location_id is required for inventory search');
    }

    let params = new HttpParams();
    if (filters.location_id) {
      params = params.set('location_id', filters.location_id.toString());
    }

    // Add pagination parameters
    if (filters.limit) {
      params = params.set('limit', filters.limit.toString());
    }
    
    if (filters.offset) {
      params = params.set('offset', filters.offset.toString());
    }

    // Add filter parameters
    if (filters.item_id) {
      params = params.set('item_id', filters.item_id.toString());
    }
    
    if (filters.item_name) {
      params = params.set('item_name', filters.item_name);
    }
    
    if (filters.serial_number) {
      params = params.set('serial_number', filters.serial_number);
    }

    // Default to false if not specified
    if (filters.is_deleted !== undefined) {
      params = params.set('is_deleted', filters.is_deleted.toString());
    } else {
      params = params.set('is_deleted', 'false');
    }

    if (filters.positive_only !== undefined) {
      params = params.set('positive_only', filters.positive_only.toString());
    }

    if (filters.get_services !== undefined) {
      params = params.set('get_services', filters.get_services.toString());
    }

    if (filters.all_location !== undefined) {
      params = params.set('all_location', filters.all_location.toString());
    }

    return this.http.get<InventoryApiResponse>(`${this.API_BASE_URL}/api/v1/inventories/`, { 
      params,
      withCredentials: true 
    });
  }

  /**
   * Get single inventory by ID
   * Endpoint: GET /api/v1/inventories/{id}
   */
  getInventoryById(inventoryId: string | number): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/inventories/${inventoryId}`, {
      withCredentials: true
    });
  }

  /**
   * Get inventories by item master IDs
   * Endpoint: POST /api/v1/inventories/by-item-master-ids
   * @param itemMasterIds Array of item master IDs to fetch inventories for
   * @param locationId Mandatory location ID to filter by
   */
  getInventoriesByItemMasterIds(itemMasterIds: (string | number)[], locationId: number | string): Observable<InventoryApiResponse> {
    if (!locationId) {
      throw new Error('location_id is required for inventory search');
    }
    return this.http.post<InventoryApiResponse>(
      `${this.API_BASE_URL}/api/v1/inventories/by-item-master-ids`,
      { item_master_ids: itemMasterIds, location_id: locationId },
      { withCredentials: true }
    );
  }

  /**
   * Export inventory stock report as Excel (monthly mode)
   * @param subsidiaryIds Array of subsidiary IDs
   * @param month Month string in YYYY-MM format
   */
  exportStockReport(subsidiaryIds: number[], month: string): Observable<Blob> {
    let params = new HttpParams();
    params = params.set('subsidiary_ids', subsidiaryIds.join(','));
    params = params.set('month', month);

    return this.http.get(`${this.API_BASE_URL}/api/v1/inventory/stock-report/export`, {
      params,
      responseType: 'blob',
      withCredentials: true
    });
  }

  /**
   * Export a dump of current inventory as Excel (current mode)
   * Takes no dates — the backend reads live inventory as it stands right now.
   * @param subsidiaryIds Array of subsidiary IDs
   */
  exportCurrentStockReport(subsidiaryIds: number[]): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    params = params.set('subsidiary_ids', subsidiaryIds.join(','));
    params = params.set('report_type', 'current');

    // observe: 'response' so the caller can take the filename from
    // Content-Disposition. The server stamps the as-of date in IST; deriving it
    // from the browser clock instead would disagree across a timezone boundary.
    return this.http.get(`${this.API_BASE_URL}/api/v1/inventory/stock-report/export`, {
      params,
      responseType: 'blob',
      observe: 'response',
      withCredentials: true
    });
  }

  /**
   * Export inventory stock report as Excel (date range mode)
   * @param subsidiaryIds Array of subsidiary IDs
   * @param startDate Start date in YYYY-MM-DD format
   * @param endDate End date in YYYY-MM-DD format
   */
  exportStockReportByDateRange(subsidiaryIds: number[], startDate: string, endDate: string): Observable<Blob> {
    let params = new HttpParams();
    params = params.set('subsidiary_ids', subsidiaryIds.join(','));
    params = params.set('report_type', 'date_range');
    params = params.set('start_date', startDate);
    params = params.set('end_date', endDate);

    return this.http.get(`${this.API_BASE_URL}/api/v1/inventory/stock-report/export`, {
      params,
      responseType: 'blob',
      withCredentials: true
    });
  }
}

